import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  useTheme
} from '@mui/material'
import { useSmartTranslation } from '../../../../../generic/hooks/useSmartTranslation'

/**
 * TabPanel component for rendering tab content
 */
function TabPanel (props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
      style={{ flexGrow: 1, overflow: 'auto' }}
    >
      {value === index && (
        <Box sx={{ p: 0, height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
}

function a11yProps (index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  }
}

/**
 * JobTabs Component
 * Displays tabs for Materials, Tools, and Quality.
 *
 * @param {Object} props
 * @param {number} props.tabValue
 * @param {function} props.handleTabChange
 * @param {React.ReactNode} props.leadingAction Historia 148 (S8): el control que va a la IZQUIERDA
 * de las pestañas. Ocupa el sitio exacto del antiguo icono «Open materials sheet», que abría la
 * ventana documental de un solo tipo de recurso; hoy lo llena el botón compartido de documentación.
 * `JobTabs` no sabe qué es: solo le reserva el hueco, para no ganar dependencias de documentación.
 * @param {Array} props.materials
 * @param {Object} props.jobReference
 */
const JobTabs = ({
  tabValue,
  handleTabChange,
  leadingAction = null,
  materials = [],
  jobReference
}) => {
  const theme = useTheme()
  const { t_ } = useSmartTranslation()

  const filteredMaterials = materials
    .filter((material) => material.id === jobReference?.node?.id)
    .flatMap((material) => material.jobReference?.resources || [])

  return (
    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {leadingAction ? <Box sx={{ mr: 1 }} onMouseDown={(e) => e.stopPropagation()}>{leadingAction}</Box> : null}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label={t_('Job details tabs')}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 36,
              fontWeight: 'bold',
              fontSize: '0.875rem',
              mr: 1,
              color: 'text.secondary',
              '&.Mui-selected': {
                color: 'text.primary',
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderBottom: 'none',
                borderRadius: '4px 4px 0 0'
              }
            },
            '& .MuiTabs-indicator': {
              display: 'none'
            }
          }}
        >
          <Tab
            label={t_('Materials')}
            {...a11yProps(0)}
            sx={{
              '&.Mui-selected': {
                borderBottom: `2px solid ${theme.palette.primary.main} !important`,
                bgcolor: 'transparent !important',
                color: `${theme.palette.primary.main} !important`,
                border: 'none !important'
              }
            }}
          />
          <Tab disabled label={t_('Tools')} {...a11yProps(1)} />
          <Tab disabled label={t_('Quality')} {...a11yProps(2)} />
        </Tabs>
      </Box>

      <Box sx={{ flexGrow: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', maxHeight: 200 }}>
        <TabPanel value={tabValue} index={0} style={{ flexGrow: 1, overflowY: 'auto' }}>
          <List dense sx={{ p: 0 }}>
            {filteredMaterials.map((item, index, array) => (
              <React.Fragment key={item.resource?.code || index}>
                <ListItem
                  sx={{
                    py: 1,
                    px: 2,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <ListItemText
                    primary={
                      <Box display='flex' alignItems='center' gap={1} flexWrap='nowrap' overflow='hidden'>
                        <Typography variant='body2' fontWeight='bold' noWrap>
                          {item.resource?.code}
                        </Typography>
                        <Typography variant='body2' noWrap>
                          · {item.resource?.name}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' noWrap sx={{ ml: 'auto', flexShrink: 0 }}>
                          {item.resourceType?.name}
                        </Typography>
                      </Box>
                      }
                  />
                </ListItem>
                {index < array.length - 1 && <Divider component='li' />}
              </React.Fragment>
            ))}
            {filteredMaterials.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant='body2' color='text.secondary'>
                  {t_('No materials assigned')}
                </Typography>
              </Box>
            )}
          </List>
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              {t_('No tools assigned')}
            </Typography>
          </Box>
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography variant='body2' color='text.secondary'>
              {t_('No quality checks pending')}
            </Typography>
          </Box>
        </TabPanel>
      </Box>
    </Box>
  )
}

JobTabs.propTypes = {
  tabValue: PropTypes.number.isRequired,
  handleTabChange: PropTypes.func.isRequired,
  leadingAction: PropTypes.node,
  materials: PropTypes.array,
  jobReference: PropTypes.object
}

export default JobTabs
